import IPhoto from 'assets/icons/Pak-Tani.png'
import { P } from 'components'
import ProfilePic from 'components/PMBProfilePic'
import PMBPagination from 'components/PMBPagination'
import PMBSearchInputGroup from 'components/PMBSearchInputGroup'
import { TableComponent as DataTable } from 'components/TableComponent'
import { useDebounce } from 'helpers'
import { useAbilityAccess } from 'hooks/useAbilityAccess'
import useGetTeamsAvatarQuery from 'hooks/useGetTeamsAvatarQuery'
import { useEffect, useState } from 'react'
import { ArrowLeft } from 'react-feather'
import { Navigate, useNavigate } from 'react-router-dom'
import { Badge, Card, CardBody, CardText, ListGroupItem } from 'reactstrap'
import { URL_406 } from 'utils/pathUrl'

const TeamSetting = ({ action, subject }) => {
  const navigate = useNavigate()
  const ability = useAbilityAccess({ action, subject })
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [currentPage, setCurrentPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [sortColumn, setSortColumn] = useState('name')
  const [sortDirection, setSortDirection] = useState('asc')

  const teams = useGetTeamsAvatarQuery({
    page: currentPage,
    size: rowsPerPage,
    sortby: sortColumn ?? 'name',
    sortdir: sortDirection ?? 'asc',
    search,
  })

  const debouncedSearch = useDebounce((value) => {
    setCurrentPage(0)
    setRowsPerPage(10)
    setSearch(value)
    // teams?.refetch()
  }, 600)

  const onHandleSearchChange = (value) => {
    setSearchInput(value ?? '')
    sessionStorage.setItem('teamSettingSearch', value ?? '')
    debouncedSearch(value ?? '')
  }

  useEffect(() => {
    const cached = sessionStorage.getItem('teamSettingSearch') || ''
    if (cached && cached !== searchInput) {
      setSearchInput(cached)
      setSearch(cached)
    }
  }, [searchInput])

  const handleSort = (column, direction) => {
    setSortColumn(column.selector)
    setSortDirection(direction)
  }

  const handleKeyDown = (e) => {
    if (e.key === ' ' && search.length === 0) {
      e.preventDefault()
    }
  }

  const columns = [
    {
      name: <span>Nama</span>,
      selector: 'name',
      sortable: true,
      cell: (row) => {
        return (
          <div className="flex-centered gap-2" data-testid={`team-table-row-${row?.userId || row?.id}`}>
            <>
              <ProfilePic
                imgSrc={row?.personAvatar}
                altText={row?.name}
                name={row?.name}
                avatarSeed={row?.userId}
                width={20}
                height={20}
              />
              <span>{row?.name}</span>
            </>
          </div>
        )
      },
    },
    {
      name: <span>Email</span>,
      selector: 'email',
      sortable: true,
      cell: (row) => <span>{row?.email}</span>,
    },
    {
      name: <span>Peran</span>,
      selector: 'rolesName',
      sortable: true,
      cell: (row) => <span>{row?.rolesName}</span>,
    },
    {
      name: <span>Dokumen Kontrak</span>,
      cell: (row) => {
        const contractImgs = row?.employmentData?.contractImgs

        if (!contractImgs) {
          return <span>-</span>
        }

        return (
          <span
            className="cursor-pointer text-primary"
            style={{ textDecoration: 'none', cursor: 'pointer' }}
            onMouseEnter={(e) => (e.target.style.textDecoration = 'underline')}
            onMouseLeave={(e) => (e.target.style.textDecoration = 'none')}
            onClick={() => window.open(contractImgs, '_blank')}
          >
            Lihat Dokumen
          </span>
        )
      },
    },
    {
      name: <span>BPJS KES/KIS</span>,
      cell: (row) => {
        const healthInsurance = row?.employmentData?.health_insurance_number
        return <span>{healthInsurance ?? '-'}</span>
      },
    },
    {
      name: <span>BPJS TK</span>,
      cell: (row) => {
        const employmentInsurance =
          row?.employmentData?.employment_insurance_number
        return <span>{employmentInsurance ?? '-'}</span>
      },
    },
    {
      name: <span>Tanggal Lahir</span>,
      cell: (row) => {
        const dayOfBirth = row?.employmentData?.dayOfBirth
        return <span>{dayOfBirth ?? '-'}</span>
      },
    },
    {
      name: <span>Area Kerja</span>,
      cell: (row) => {
        const dayOfBirth = row?.employmentData?.work_area ?? row?.address ?? '-'
        return <span>{dayOfBirth ?? '-'}</span>
      },
    },
    {
      name: <span>Status</span>,
      cell: (row) => {
        return !row?.active ? (
          <Badge className="badge-danger">Nonaktif</Badge>
        ) : (
          <Badge className="badge-verify">Aktif</Badge>
        )
      },
    },
  ]

  if (!ability) {
    return <Navigate to={URL_406} replace={true} />
  }

  return (
    <CardBody>
      <ListGroupItem
        className="py-4 px-3"
        style={{ backgroundColor: 'white', borderBottom: '1px solid #dee2e6' }}
      >
        <div className="d-flex justify-content-between align-items-center">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <ArrowLeft
              size={20}
              style={{ cursor: 'pointer' }}
              onClick={() => {
                navigate(-1)
              }}
            />
            <div>
              <P size="xlarge">Tim Saya</P>
            </div>
          </div>
        </div>
      </ListGroupItem>
      <div className="p-3">
        <Card
          className="px-3"
          style={{
            border: 'none',
          }}
        >
          <CardText tag="p" className="mt-3">
            Daftar anggota yang bergabung dengan tim
          </CardText>
          <div className="">
            <PMBSearchInputGroup
              label={'Cari'}
              testId="team-search-input"
              value={searchInput}
              placeholder="Cari nama tim..."
              onChange={(e) => onHandleSearchChange(e.target.value)}
              minWidth="15%"
              onKeyDown={handleKeyDown}
            />
          </div>
          <CardBody className="mt-4">
            <DataTable
              columns={columns}
              data={teams?.data?.items}
              progressPending={teams?.isFetching ?? teams?.isLoading}
              paginationTotalRows={teams?.data?.totalItems ?? []}
              className="border p-0 border-1 rounded-top"
              pagination
              paginationServer
              responsive
              persistTableHead
              defaultSortFieldId={1}
              defaultSortAsc={true}
              sortServer
              onSort={handleSort}
              noDataComponent={
                <p className="my-3 fw-semibold">
                  {searchInput !== '' &&
                  !teams?.data?.items?.length &&
                  !teams?.isFetching
                    ? 'Tidak ada data yang sesuai dengan filter Anda.'
                    : 'Belum ada daftar tim yang didaftarkan'}
                </p>
              }
              paginationComponent={(props) => {
                return (
                  <PMBPagination
                    rowCount={teams?.data?.totalItems || 0}
                    currentPage={currentPage}
                    rowsPerPage={rowsPerPage}
                    setCurrentPage={setCurrentPage}
                    setRowsPerPage={setRowsPerPage}
                    props={props}
                  />
                )
              }}
            />
          </CardBody>
        </Card>
      </div>
    </CardBody>
  )
}

export default TeamSetting
